/**
 * 재원 마스터 PoC (tenant·회계연도별 localStorage / BdFund API)
 * 계약: D:\295\erp\BD\design\04_재원_구성계약.md
 * - 재원(최소 저장 단위) / 재원그룹(조회 합산) / 재원구성(비율 패키지)
 * - 수입과목 ↔ 재원: 예산과목등록 속성 `fundUid` (별도 매핑 화면 없음)
 * - Fund API는 fiscalYear 없음 — tenantCode 만 사용
 */

import { isBdApiEnabled } from 'config/bdApiConfig';
import { commonSelect, commonSaveList, cacheGet, cacheSet } from 'services/commonQuery';
import { saveFundPackagesApi } from 'budget/fundPackageApi';

export const FUND_GROUP_PREFIX = 'budgetFundGroup';
export const FUND_PREFIX = 'budgetFund';
export const FUND_HISTORY_PREFIX = 'budgetFundHistory';
export const FUND_PKG_PREFIX = 'budgetFundPackage';
// 수입↔재원 매핑은 별도 테이블 없이 수입과목 속성 `fundUid` (예산과목등록) 사용

export const YN_OPTIONS = [
  { value: 'Y', label: 'Y' },
  { value: 'N', label: 'N' },
];

/** 보고서·기관 간 비교용 표준 대분류 (선택) */
export const REPORT_CLASS_OPTIONS = [
  { value: 'NAT', label: '국비' },
  { value: 'LOC', label: '지방비' },
  { value: 'OWN', label: '자체' },
  { value: 'DON', label: '후원' },
  { value: 'ETC', label: '기타' },
];

export const FUND_CHANGE_TYPE = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  GROUP: 'GROUP',
  CLOSE: 'CLOSE',
};

export const FUND_CHANGE_TYPE_LABEL = {
  CREATE: '신규',
  UPDATE: '수정',
  GROUP: '그룹변경',
  CLOSE: '폐지',
};

export function createFundUid(prefix = 'FU') {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function nextSortOrderBy10(rows) {
  let max = 0;
  for (const r of rows || []) {
    const n = Number(r?.sortOrder);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return Math.ceil((max + 1) / 10) * 10;
}

function dimsKey({ tenantCode, fiscalYear }) {
  return `${tenantCode || ''}|${fiscalYear || ''}`;
}

/** API 키 — 재원은 기관 단위(연도 없음) */
function tenantOnly(dims) {
  return String(dims?.tenantCode || '').trim();
}

const fundGroupCache = new Map();
const fundCache = new Map();
const fundPkgCache = new Map();

function storageKey(prefix, dims) {
  return `${prefix}|${dimsKey(dims)}`;
}

function readRows(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return Array.isArray(data?.rows) ? data.rows : null;
  } catch {
    return null;
  }
}

function writeRows(key, dims, rows) {
  localStorage.setItem(
    key,
    JSON.stringify({
      dims: { tenantCode: dims.tenantCode, fiscalYear: dims.fiscalYear },
      rows: rows || [],
      savedAt: new Date().toISOString(),
    })
  );
}

function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function reportClassLabel(code) {
  return REPORT_CLASS_OPTIONS.find((o) => o.value === code)?.label || code || '';
}

export function changeTypeLabel(code) {
  return FUND_CHANGE_TYPE_LABEL[code] || code || '';
}

/* ─── 재원그룹 ─── */

export function fundGroupStorageKey(dims) {
  return storageKey(FUND_GROUP_PREFIX, dims);
}

export function buildSampleFundGroups() {
  return [
    {
      fundGroupUid: createFundUid('FG'),
      groupCode: 'SUBSIDY',
      groupName: '보조금',
      sortOrder: 10,
      useYn: 'Y',
      remark: '국비·도비·시비·기타보조금',
      mod: '',
    },
    {
      fundGroupUid: createFundUid('FG'),
      groupCode: 'DONATION',
      groupName: '후원금',
      sortOrder: 20,
      useYn: 'Y',
      remark: '',
      mod: '',
    },
    {
      fundGroupUid: createFundUid('FG'),
      groupCode: 'OWN',
      groupName: '자체',
      sortOrder: 30,
      useYn: 'Y',
      remark: '',
      mod: '',
    },
    {
      fundGroupUid: createFundUid('FG'),
      groupCode: 'ETC',
      groupName: '기타',
      sortOrder: 40,
      useYn: 'Y',
      remark: '',
      mod: '',
    },
  ];
}

export async function loadFundGroups(dims) {
  const tc = tenantOnly(dims);
  if (isBdApiEnabled()) {
    const rows = await commonSelect('BdFund.selectFundGroupList', { tenantCode: tc });
    const mapped = (rows || []).map((r) => ({
      ...r,
      fundGroupUid: r.fundGroupUid || createFundUid('FG'),
      groupCode: r.groupCode || r.fundGroupCode || '',
      groupName: r.groupName || r.fundGroupNm || '',
      mod: '',
    }));
    cacheSet(fundGroupCache, tc, mapped);
    return mapped;
  }
  const key = fundGroupStorageKey(dims);
  const rows = readRows(key);
  if (rows) {
    const mapped = rows.map((r) => ({ ...r, fundGroupUid: r.fundGroupUid || createFundUid('FG') }));
    cacheSet(fundGroupCache, tc, mapped);
    return mapped;
  }
  const sample = buildSampleFundGroups();
  writeRows(key, dims, sample);
  cacheSet(fundGroupCache, tc, sample);
  return sample;
}

export async function saveFundGroups(dims, rows) {
  const tc = tenantOnly(dims);
  const normalized = (rows || []).map((r) => ({
    ...r,
    fundGroupUid: r.fundGroupUid || createFundUid('FG'),
    groupCode: String(r.groupCode || r.fundGroupCode || '').trim(),
    groupName: String(r.groupName || r.fundGroupNm || '').trim(),
  }));

  if (isBdApiEnabled()) {
    const dirty = normalized
      .filter((r) => r.mod === 'A' || r.mod === 'U' || r.mod === 'D')
      .map((r) => ({
        ...r,
        fundGroupCode: r.groupCode,
        fundGroupNm: r.groupName,
      }));
    await commonSaveList(
      ['BdFund.insertFundGroup', 'BdFund.updateFundGroup', 'BdFund.deleteFundGroup'],
      dirty,
      { tenantCode: tc }
    );
    return loadFundGroups(dims);
  }

  writeRows(fundGroupStorageKey(dims), dims, normalized);
  cacheSet(fundGroupCache, tc, normalized);
  return normalized;
}

export function filterFundGroups(rows, q = {}) {
  return (rows || []).filter((r) => {
    if (q.groupCode && !String(r.groupCode || '').includes(String(q.groupCode))) return false;
    if (q.groupName && !String(r.groupName || '').includes(String(q.groupName))) return false;
    if (q.useYn && r.useYn !== q.useYn) return false;
    return true;
  });
}

export function validateFundGroups(rows = []) {
  const active = (rows || []).filter((r) => r.mod !== 'D');
  const codes = new Set();
  for (const r of active) {
    if (!String(r.groupCode || '').trim()) {
      return { ok: false, message: '재원그룹코드를 입력하세요.' };
    }
    if (!String(r.groupName || '').trim()) {
      return { ok: false, message: '재원그룹명을 입력하세요.' };
    }
    const c = String(r.groupCode).trim();
    if (codes.has(c)) return { ok: false, message: `재원그룹코드 중복: ${c}` };
    codes.add(c);
  }
  return { ok: true };
}

export function fundGroupOptions(dims, { includeEmpty = false } = {}) {
  const tc = tenantOnly(dims);
  const rows = cacheGet(fundGroupCache, tc) || [];
  const opts = rows
    .filter((r) => r.useYn !== 'N')
    .sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
    .map((r) => ({ value: r.fundGroupUid, label: `${r.groupCode} ${r.groupName}` }));
  return includeEmpty ? [{ value: '', label: '선택' }, ...opts] : opts;
}

/* ─── 재원 ─── */

export function fundStorageKey(dims) {
  return storageKey(FUND_PREFIX, dims);
}

export function fundHistoryKey(tenantCode, fundUid) {
  return `${FUND_HISTORY_PREFIX}|${tenantCode || ''}|${fundUid || ''}`;
}

export function enrichFundRows(rows, groups = []) {
  const byUid = new Map((groups || []).map((g) => [g.fundGroupUid, g]));
  return (rows || []).map((r) => {
    const g = byUid.get(r.fundGroupUid);
    const fundUid = r.fundUid || createFundUid('FU');
    return {
      ...r,
      fundUid,
      // HiGrid getRowId / 삭제 선택 안정화
      id: r.id || fundUid,
      fundGroupNm: g ? `${g.groupCode} ${g.groupName}` : '',
      reportClassNm: reportClassLabel(r.reportClass),
    };
  });
}

export function buildSampleFunds(groups = []) {
  const byCode = new Map((groups || []).map((g) => [g.groupCode, g.fundGroupUid]));
  const g = (code) => byCode.get(code) || '';
  return [
    { fundUid: createFundUid('FU'), fundCode: 'NAT', fundName: '국비', fundGroupUid: g('SUBSIDY'), reportClass: 'NAT', sortOrder: 10, useYn: 'Y', extCode: '', remark: '' },
    { fundUid: createFundUid('FU'), fundCode: 'PROV', fundName: '도비', fundGroupUid: g('SUBSIDY'), reportClass: 'LOC', sortOrder: 20, useYn: 'Y', extCode: '', remark: '' },
    { fundUid: createFundUid('FU'), fundCode: 'CITY', fundName: '시비', fundGroupUid: g('SUBSIDY'), reportClass: 'LOC', sortOrder: 30, useYn: 'Y', extCode: '', remark: '' },
    { fundUid: createFundUid('FU'), fundCode: 'ETC_SUB', fundName: '기타보조금', fundGroupUid: g('SUBSIDY'), reportClass: 'ETC', sortOrder: 40, useYn: 'Y', extCode: '', remark: '보조금 그룹에 포함' },
    { fundUid: createFundUid('FU'), fundCode: 'DON1', fundName: '후원금1', fundGroupUid: g('DONATION'), reportClass: 'DON', sortOrder: 50, useYn: 'Y', extCode: '', remark: '' },
    { fundUid: createFundUid('FU'), fundCode: 'DON2', fundName: '후원금2', fundGroupUid: g('DONATION'), reportClass: 'DON', sortOrder: 60, useYn: 'Y', extCode: '', remark: '' },
    { fundUid: createFundUid('FU'), fundCode: 'OWN', fundName: '자체', fundGroupUid: g('OWN'), reportClass: 'OWN', sortOrder: 70, useYn: 'Y', extCode: '', remark: '' },
  ].map((r) => ({ ...r, mod: '' }));
}

export async function loadFunds(dims) {
  const tc = tenantOnly(dims);
  const groups = await loadFundGroups(dims);
  if (isBdApiEnabled()) {
    const rows = await commonSelect('BdFund.selectFundList', { tenantCode: tc });
    const mapped = enrichFundRows(rows || [], groups).map((r) => ({ ...r, mod: '' }));
    cacheSet(fundCache, tc, mapped);
    return mapped;
  }
  const key = fundStorageKey(dims);
  const rows = readRows(key);
  if (rows) {
    const mapped = enrichFundRows(rows, groups).map((r) => ({ ...r, mod: '' }));
    cacheSet(fundCache, tc, mapped);
    return mapped;
  }
  const sample = enrichFundRows(buildSampleFunds(groups), groups);
  writeRows(key, dims, sample);
  cacheSet(fundCache, tc, sample);
  return sample;
}

export async function saveFunds(dims, rows) {
  const tc = tenantOnly(dims);
  const groups = await loadFundGroups(dims);

  if (isBdApiEnabled()) {
    const dirty = (rows || [])
      .filter((r) => r.mod === 'A' || r.mod === 'U' || r.mod === 'D')
      .map((r) => ({
        ...r,
        fundUid: r.fundUid || createFundUid('FU'),
        fundName: r.fundName || r.fundNm || '',
      }));
    await commonSaveList(
      ['BdFund.insertFund', 'BdFund.updateFund', 'BdFund.deleteFund'],
      dirty,
      { tenantCode: tc }
    );
    return loadFunds(dims);
  }

  const normalized = enrichFundRows(rows || [], groups)
    .filter((r) => r.mod !== 'D')
    .map((r) => ({ ...r, mod: '' }));
  writeRows(fundStorageKey(dims), dims, normalized);
  cacheSet(fundCache, tc, normalized);
  return normalized;
}

export function filterFunds(rows, q = {}) {
  return (rows || []).filter((r) => {
    if (q.fundCode && !String(r.fundCode || '').includes(String(q.fundCode))) return false;
    if (q.fundName && !String(r.fundName || '').includes(String(q.fundName))) return false;
    if (q.fundGroupUid && r.fundGroupUid !== q.fundGroupUid) return false;
    if (q.reportClass && r.reportClass !== q.reportClass) return false;
    if (q.useYn && r.useYn !== q.useYn) return false;
    return true;
  });
}

export function validateFunds(rows = [], groups = []) {
  const active = (rows || []).filter((r) => r.mod !== 'D');
  const groupUids = new Set((groups || []).map((g) => g.fundGroupUid));
  const codes = new Set();
  for (const r of active) {
    if (!String(r.fundCode || '').trim()) return { ok: false, message: '재원코드를 입력하세요.' };
    if (!String(r.fundName || '').trim()) return { ok: false, message: '재원명을 입력하세요.' };
    if (!r.fundGroupUid) return { ok: false, message: '재원그룹을 선택하세요.' };
    if (groupUids.size && !groupUids.has(r.fundGroupUid)) {
      return { ok: false, message: `유효하지 않은 재원그룹: ${r.fundCode}` };
    }
    const c = String(r.fundCode).trim();
    if (codes.has(c)) return { ok: false, message: `재원코드 중복: ${c}` };
    codes.add(c);
  }
  return { ok: true };
}

export function fundOptions(dims, { includeEmpty = false } = {}) {
  const tc = tenantOnly(dims);
  const rows = cacheGet(fundCache, tc) || [];
  const opts = rows
    .filter((r) => r.useYn !== 'N')
    .sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
    .map((r) => ({ value: r.fundUid, label: `${r.fundCode} ${r.fundName}` }));
  return includeEmpty ? [{ value: '', label: '선택' }, ...opts] : opts;
}

/** 동기 lookup용 — loadFunds / loadFundPackages 이후 캐시 */
export function getCachedFunds(dims) {
  return cacheGet(fundCache, tenantOnly(dims)) || [];
}

export function getCachedFundPackages(dims) {
  return cacheGet(fundPkgCache, tenantOnly(dims)) || [];
}

/** 그룹별 재원 합산 조회용 */
export function sumByFundGroup(fundAmounts, funds) {
  const byUid = new Map((funds || []).map((f) => [f.fundUid, f]));
  const totals = new Map();
  for (const a of fundAmounts || []) {
    const f = byUid.get(a.fundUid);
    if (!f?.fundGroupUid) continue;
    totals.set(f.fundGroupUid, (totals.get(f.fundGroupUid) || 0) + Number(a.amount || 0));
  }
  return totals;
}

function snapshotFund(row) {
  return {
    fundCode: row.fundCode || '',
    fundName: row.fundName || '',
    fundGroupUid: row.fundGroupUid || '',
    reportClass: row.reportClass || '',
    useYn: row.useYn || 'Y',
    sortOrder: row.sortOrder ?? '',
    extCode: row.extCode || '',
    remark: row.remark || '',
  };
}

export function loadFundHistory(tenantCode, fundUid) {
  if (!fundUid) return [];
  try {
    const raw = localStorage.getItem(fundHistoryKey(tenantCode, fundUid));
    if (!raw) return [];
    const data = JSON.parse(raw);
    const rows = Array.isArray(data?.rows) ? data.rows : [];
    return rows
      .map((r) => ({
        ...r,
        changeTypeNm: changeTypeLabel(r.changeType),
        changeAtDisp: formatDateTime(r.changeAt),
        reportClassNm: reportClassLabel(r.snapshot?.reportClass),
      }))
      .sort((a, b) => String(b.changeAt || '').localeCompare(String(a.changeAt || '')));
  } catch {
    return [];
  }
}

function appendFundHistory(tenantCode, fundUid, entry) {
  if (!fundUid || !entry) return;
  const prev = loadFundHistory(tenantCode, fundUid).map(
    ({ changeTypeNm, changeAtDisp, reportClassNm, ...rest }) => rest
  );
  const next = [
    {
      histId: createFundUid('FH'),
      changeType: entry.changeType || FUND_CHANGE_TYPE.UPDATE,
      changeAt: entry.changeAt || new Date().toISOString(),
      changeBy: entry.changeBy || '',
      memo: entry.memo || '',
      snapshot: entry.snapshot || {},
    },
    ...prev,
  ];
  localStorage.setItem(
    fundHistoryKey(tenantCode, fundUid),
    JSON.stringify({ tenantCode, fundUid, rows: next, savedAt: new Date().toISOString() })
  );
}

/**
 * 재원 저장 + 이력(신규·수정·그룹변경·폐지)
 * @returns {{ saved, historyTouched }}
 */
export async function persistFundsWithHistory(dims, nextRows, { userId } = {}) {
  const tenant = dims.tenantCode || '';
  const prev = isBdApiEnabled()
    ? cacheGet(fundCache, tenantOnly(dims)) || []
    : readRows(fundStorageKey(dims)) || [];
  const prevMap = new Map(prev.map((r) => [r.fundUid, r]));
  const nextMap = new Map((nextRows || []).map((r) => [r.fundUid, r]));
  let historyTouched = 0;

  for (const row of nextRows || []) {
    const old = prevMap.get(row.fundUid);
    if (!old) {
      appendFundHistory(tenant, row.fundUid, {
        changeType: FUND_CHANGE_TYPE.CREATE,
        changeBy: userId,
        snapshot: snapshotFund(row),
        memo: '신규',
      });
      historyTouched += 1;
      continue;
    }
    const groupChanged = String(old.fundGroupUid || '') !== String(row.fundGroupUid || '');
    const changed =
      groupChanged ||
      String(old.fundCode) !== String(row.fundCode) ||
      String(old.fundName) !== String(row.fundName) ||
      String(old.reportClass || '') !== String(row.reportClass || '') ||
      String(old.useYn) !== String(row.useYn) ||
      String(old.extCode || '') !== String(row.extCode || '') ||
      Number(old.sortOrder) !== Number(row.sortOrder) ||
      String(old.remark || '') !== String(row.remark || '');
    if (!changed) continue;
    appendFundHistory(tenant, row.fundUid, {
      changeType: groupChanged ? FUND_CHANGE_TYPE.GROUP : FUND_CHANGE_TYPE.UPDATE,
      changeBy: userId,
      snapshot: snapshotFund(row),
      memo: groupChanged ? '재원그룹 변경' : '속성 수정',
    });
    historyTouched += 1;
  }

  for (const [uid, old] of prevMap) {
    if (nextMap.has(uid)) continue;
    appendFundHistory(tenant, uid, {
      changeType: FUND_CHANGE_TYPE.CLOSE,
      changeBy: userId,
      snapshot: snapshotFund(old),
      memo: '삭제(폐지)',
    });
    historyTouched += 1;
  }

  const saved = await saveFunds(dims, nextRows);
  return { saved, historyTouched };
}

/* ─── 재원구성(패키지) ─── */

export function fundPkgStorageKey(dims) {
  return storageKey(FUND_PKG_PREFIX, dims);
}

export function defaultPkgMember(partial = {}) {
  return {
    memberId: createFundUid('PM'),
    fundUid: '',
    ratioBp: 0,
    amount: null,
    /** 비율 안분 시 끝전(잔액)을 흡수할 재원 — 구성원 중 1건만 Y */
    remainderYn: 'N',
    ...partial,
  };
}

export function defaultFundPackage(partial = {}) {
  return {
    fundPkgUid: createFundUid('FP'),
    pkgCode: '',
    pkgName: '',
    sortOrder: 10,
    useYn: 'Y',
    remark: '',
    members: [],
    mod: '',
    ...partial,
  };
}

export function buildSamplePackages(funds = []) {
  const byCode = new Map((funds || []).map((f) => [f.fundCode, f.fundUid]));
  const uid = (c) => byCode.get(c) || '';
  return [
    {
      ...defaultFundPackage({
        pkgCode: 'N50P50',
        pkgName: '국비50:도비50',
        sortOrder: 10,
        members: [
          defaultPkgMember({ fundUid: uid('NAT'), ratioBp: 5000, remainderYn: 'Y' }),
          defaultPkgMember({ fundUid: uid('PROV'), ratioBp: 5000, remainderYn: 'N' }),
        ],
      }),
    },
    {
      ...defaultFundPackage({
        pkgCode: '5-2-2-1',
        pkgName: '국비5:도비2:시비2:자체1',
        sortOrder: 20,
        members: [
          defaultPkgMember({ fundUid: uid('NAT'), ratioBp: 5000, remainderYn: 'N' }),
          defaultPkgMember({ fundUid: uid('PROV'), ratioBp: 2000, remainderYn: 'N' }),
          defaultPkgMember({ fundUid: uid('CITY'), ratioBp: 2000, remainderYn: 'N' }),
          defaultPkgMember({ fundUid: uid('OWN'), ratioBp: 1000, remainderYn: 'Y' }),
        ],
      }),
    },
  ];
}

export function enrichPackageRows(rows, funds = []) {
  const byUid = new Map((funds || []).map((f) => [f.fundUid, f]));
  return (rows || []).map((pkg) => {
    let members = (pkg.members || []).map((m) => {
      const f = byUid.get(m.fundUid);
      return {
        ...m,
        memberId: m.memberId || createFundUid('PM'),
        fundCode: f?.fundCode || '',
        fundName: f?.fundName || '',
        ratioPct: m.ratioBp != null ? Number(m.ratioBp) / 100 : '',
        remainderYn: m.remainderYn === 'Y' ? 'Y' : 'N',
      };
    });
    // 구데이터·미지정: 활성 구성원 중 끝전이 없으면 마지막 행에 지정 (기존 안분 호환)
    const active = members.filter((m) => m.mod !== 'D');
    if (active.length && !active.some((m) => m.remainderYn === 'Y')) {
      const lastId = active[active.length - 1].memberId;
      members = members.map((m) =>
        m.mod !== 'D' && m.memberId === lastId ? { ...m, remainderYn: 'Y' } : m
      );
    }
    const ratioSum = members.reduce((s, m) => s + Number(m.ratioBp || 0), 0);
    return {
      ...pkg,
      fundPkgUid: pkg.fundPkgUid || createFundUid('FP'),
      members,
      memberCount: members.length,
      ratioSumBp: ratioSum,
      ratioSumDisp: `${(ratioSum / 100).toFixed(1)}%`,
    };
  });
}

export async function loadFundPackages(dims) {
  const tc = tenantOnly(dims);
  const funds = await loadFunds(dims);
  if (isBdApiEnabled()) {
    const pkgs = await commonSelect('BdFund.selectFundPackageList', { tenantCode: tc });
    const withMembers = [];
    for (const p of pkgs || []) {
      const members = await commonSelect('BdFund.selectFundPkgMemberList', {
        fundPkgUid: p.fundPkgUid,
      });
      withMembers.push({
        ...p,
        pkgCode: p.pkgCode || p.fundPkgCode || '',
        pkgName: p.pkgName || p.fundPkgNm || '',
        members: (members || []).map((m) => ({
          ...m,
          memberId: m.memberId || m.memberUid || createFundUid('PM'),
          mod: '',
        })),
        mod: '',
      });
    }
    const mapped = enrichPackageRows(withMembers, funds).map((p) => ({
      ...p,
      mod: '',
      members: (p.members || []).map((m) => ({ ...m, mod: '' })),
    }));
    cacheSet(fundPkgCache, tc, mapped);
    return mapped;
  }
  const key = fundPkgStorageKey(dims);
  const rows = readRows(key);
  if (rows) {
    const mapped = enrichPackageRows(rows, funds).map((p) => ({
      ...p,
      mod: '',
      members: (p.members || []).map((m) => ({ ...m, mod: '' })),
    }));
    cacheSet(fundPkgCache, tc, mapped);
    return mapped;
  }
  const sample = enrichPackageRows(buildSamplePackages(funds), funds);
  writeRows(key, dims, sample);
  cacheSet(fundPkgCache, tc, sample);
  return sample;
}

export async function saveFundPackages(dims, rows) {
  const tc = tenantOnly(dims);
  const funds = await loadFunds(dims);

  if (isBdApiEnabled()) {
    const list = (rows || []).map((r) => ({
      ...r,
      fundPkgUid: r.fundPkgUid || createFundUid('FP'),
      fundPkgCode: r.pkgCode || r.fundPkgCode || '',
      fundPkgNm: r.pkgName || r.fundPkgNm || '',
    }));
    await saveFundPackagesApi(list);
    return loadFundPackages(dims);
  }

  const normalized = enrichPackageRows(rows || [], funds)
    .filter((pkg) => pkg.mod !== 'D')
    .map((pkg) => ({
      ...pkg,
      mod: '',
      members: (pkg.members || [])
        .filter((m) => m.mod !== 'D')
        .map((m) => ({
          memberId: m.memberId || createFundUid('PM'),
          fundUid: m.fundUid,
          ratioBp: Number(m.ratioBp) || 0,
          amount: m.amount == null || m.amount === '' ? null : Number(m.amount),
          remainderYn: m.remainderYn === 'Y' ? 'Y' : 'N',
          mod: '',
        })),
    }));
  writeRows(fundPkgStorageKey(dims), dims, normalized);
  const enriched = enrichPackageRows(normalized, funds).map((p) => ({
    ...p,
    mod: '',
    members: (p.members || []).map((m) => ({ ...m, mod: '' })),
  }));
  cacheSet(fundPkgCache, tc, enriched);
  return enriched;
}

export function filterFundPackages(rows, q = {}) {
  return (rows || []).filter((r) => {
    if (q.pkgCode && !String(r.pkgCode || '').includes(String(q.pkgCode))) return false;
    if (q.pkgName && !String(r.pkgName || '').includes(String(q.pkgName))) return false;
    if (q.useYn && r.useYn !== q.useYn) return false;
    return true;
  });
}

export function validateFundPackages(rows = []) {
  const active = (rows || []).filter((r) => r.mod !== 'D');
  const codes = new Set();
  for (const pkg of active) {
    if (!String(pkg.pkgCode || '').trim()) return { ok: false, message: '구성코드를 입력하세요.' };
    if (!String(pkg.pkgName || '').trim()) return { ok: false, message: '구성명을 입력하세요.' };
    const c = String(pkg.pkgCode).trim();
    if (codes.has(c)) return { ok: false, message: `구성코드 중복: ${c}` };
    codes.add(c);
    const members = (pkg.members || []).filter((m) => m.mod !== 'D');
    if (!members.length) return { ok: false, message: `${c}: 구성원을 1건 이상 등록하세요.` };
    const fundSet = new Set();
    let sum = 0;
    let remainderCnt = 0;
    for (const m of members) {
      if (!m.fundUid) return { ok: false, message: `${c}: 재원을 선택하세요.` };
      if (fundSet.has(m.fundUid)) return { ok: false, message: `${c}: 동일 재원이 중복됩니다.` };
      fundSet.add(m.fundUid);
      sum += Number(m.ratioBp || 0);
      if (m.remainderYn === 'Y') remainderCnt += 1;
    }
    if (sum !== 10000) {
      return {
        ok: false,
        message: `${c}: 비율 합계가 100%가 아닙니다. (현재 ${(sum / 100).toFixed(1)}%)`,
      };
    }
    if (remainderCnt !== 1) {
      return {
        ok: false,
        message: `${c}: 끝전처리 재원을 1건만 지정하세요. (현재 ${remainderCnt}건)`,
      };
    }
  }
  return { ok: true };
}

/**
 * 총액 × 비율 → 재원별 금액
 * 끝전(반올림 잔액)은 remainderYn='Y' 재원에 흡수. 미지정이면 마지막 재원(하위호환).
 */
export function expandPackageAmounts(pkg, totalAmount) {
  const members = (pkg?.members || []).filter((m) => m.mod !== 'D' && m.fundUid);
  const total = Number(totalAmount) || 0;
  if (!members.length) return [];
  let remIdx = members.findIndex((m) => m.remainderYn === 'Y');
  if (remIdx < 0) remIdx = members.length - 1;

  let allocated = 0;
  const out = members.map((m, idx) => {
    if (idx === remIdx) {
      return {
        fundUid: m.fundUid,
        amount: 0,
        ratioBp: Number(m.ratioBp || 0),
        remainderYn: 'Y',
      };
    }
    const amount = Math.round((total * Number(m.ratioBp || 0)) / 10000);
    allocated += amount;
    return {
      fundUid: m.fundUid,
      amount,
      ratioBp: Number(m.ratioBp || 0),
      remainderYn: 'N',
    };
  });
  out[remIdx] = {
    ...out[remIdx],
    amount: total - allocated,
    remainderYn: 'Y',
  };
  return out;
}
